import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
    Button,
    type ButtonProps,
    IconDownload,
    List,
    ListItem,
    ListSection,
    Popup,
} from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import {
    type OpenApiGetAllProductsCountsResponseSuccess,
    type OpenApiGetProductVariantsByRequestRequest,
} from '#shared/api/schema';
import { type LazyBlobExportHookData } from '#shared/components/product';
import { isNodeOrAncestor } from '#shared/lib/dom';
import { showError } from '#shared/lib/toast';
import { useLazyExportProducts, useLazyGetProductCountAll } from '../../api';

export type ProductVariantsExportQuery = {
    payload: OpenApiGetProductVariantsByRequestRequest;
};

export type ProductVariantsExportButtonProps = {
    query: ProductVariantsExportQuery;
    countKey?: Extract<keyof OpenApiGetAllProductsCountsResponseSuccess, 'archived_variants_count' | 'variants_count'>;
    color?: ButtonProps['color'];
    size?: ButtonProps['size'];
    popupWidth?: number;
    renderAdditionalActions?: (closePopup: VoidFunction) => ReactNode;
    renderExportModal: (props: {
        lazyHookData: LazyBlobExportHookData;
        isOpen: boolean;
        count: number | undefined;
        onClose: VoidFunction;
    }) => ReactNode;
} & Omit<ButtonProps, 'target' | 'icon' | 'children' | 'href' | 'type' | 'onClick' | 'isLoading' | 'color' | 'size'>;

type ProductVariantsExportModalProps = {
    query: ProductVariantsExportQuery;
    format: 'xlsx' | 'csv';
    isOpen: boolean;
    count: number | undefined;
    onClose: VoidFunction;
    renderExportModal: ProductVariantsExportButtonProps['renderExportModal'];
};

const ProductVariantsExportModal = ({
    query,
    format,
    isOpen,
    count,
    onClose,
    renderExportModal,
}: ProductVariantsExportModalProps) => {
    const [fetchExport, exportState] = useLazyExportProducts();

    return (
        <>
            {renderExportModal({
                isOpen,
                count,
                onClose,
                lazyHookData: {
                    fetch: () =>
                        fetchExport({
                            body: {
                                request: query.payload,
                            },
                            queryParameters: {
                                format,
                            },
                        }),
                    ...exportState,
                },
            })}
        </>
    );
};

export const ProductVariantsExportButton = ({
    query,
    countKey = 'variants_count',
    renderAdditionalActions,
    renderExportModal,
    size = 's',
    color = 'contour',
    popupWidth = 390,
    ...buttonProps
}: ProductVariantsExportButtonProps) => {
    const downloadButtonRef = useRef<HTMLButtonElement | null>(null);

    const [fetchCount, { data: allCount, status, isFetching: isCountFetching, isSuccess, isError }] =
        useLazyGetProductCountAll({});

    const { value: isDownloadPopupOpen, toggleOff: closeDownloadPopup, toggle: toggleDownloadPopup } = useToggle(false);
    const { value: isDataFetched, toggleOff: clearFetchedData, toggleOn: setFetchData } = useToggle();
    const {
        value: isReadyToShowModalOpen,
        toggleOff: closeReadyToShowModal,
        toggleOn: openReadyToShowModal,
    } = useToggle(false);

    const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx');

    const onFinishRequestShowModal = useCallback(() => {
        if (status === 'uninitialized' || status === 'pending') {
            return;
        }

        if (isSuccess) {
            setFetchData();
        } else if (isError) {
            showError({ label: 'Произошла ошибка при загрузке числа товаров' });
        }
    }, [status, isSuccess, isError, setFetchData]);

    useEffect(() => {
        onFinishRequestShowModal();
    }, [onFinishRequestShowModal]);

    const onCloseModal = useCallback(() => {
        closeReadyToShowModal();
        clearFetchedData();
    }, [closeReadyToShowModal, clearFetchedData]);

    const handleDownloadVariantsExcel = useCallback(() => {
        setExportFormat('xlsx');
        closeDownloadPopup();
        openReadyToShowModal();
        fetchCount(query);
    }, [fetchCount, query, closeDownloadPopup, openReadyToShowModal]);

    const handleDownloadVariantsCsv = useCallback(() => {
        setExportFormat('csv');
        closeDownloadPopup();
        openReadyToShowModal();
        fetchCount(query);
    }, [fetchCount, query, closeDownloadPopup, openReadyToShowModal]);

    const closePopupOnClickOutside = useCallback(
        (clickedElement: HTMLElement) => {
            if (!isNodeOrAncestor(downloadButtonRef.current, clickedElement)) {
                closeDownloadPopup();
            }

            return true;
        },
        [closeDownloadPopup]
    );

    return (
        <>
            <Button
                {...buttonProps}
                ref={downloadButtonRef}
                size={size}
                color={color}
                onClick={toggleDownloadPopup}
                isLoading={isCountFetching}
                icon={IconDownload}
                type="button"
            />
            <Popup
                maxWidth={popupWidth}
                autoFocusProps={{ autoFocus: false }}
                isOpen={isDownloadPopupOpen}
                placement="bottom-end"
                offset={2}
                shouldCloseOnInteractOutside={closePopupOnClickOutside}
                triggerRef={downloadButtonRef}
            >
                <List width={popupWidth} size="m" data-testid="ProductVariantsExportButton.ActionsList">
                    <ListSection>
                        <ListItem onClick={handleDownloadVariantsExcel}>Скачать товары в Excel</ListItem>
                        <ListItem onClick={handleDownloadVariantsCsv}>Скачать товары в CSV</ListItem>
                    </ListSection>
                    {renderAdditionalActions?.(closeDownloadPopup)}
                </List>
            </Popup>
            {isCountFetching ? null : (
                <ProductVariantsExportModal
                    query={query}
                    format={exportFormat}
                    isOpen={isDataFetched && isReadyToShowModalOpen}
                    count={allCount?.[countKey]}
                    onClose={onCloseModal}
                    renderExportModal={renderExportModal}
                />
            )}
        </>
    );
};
